
import { useState } from 'react';

export default function TaxAgency() {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedGuide, setSelectedGuide] = useState<string | null>(null);

  const serviceTypes = [
    {
      id: 'basic-accounting',
      title: '基础财务服务',
      description: '账务处理、账簿管理、财务报表编制等基础服务',
      icon: 'ri-file-list-3-line',
      timeframe: '按月服务',
      price: '300-800元/月',
      features: ['账务处理', '账簿管理', '财务报表', '财务审计支持']
    },
    {
      id: 'tax-services',
      title: '税务服务',
      description: '税务申报、税务合规、税务咨询等专业服务',
      icon: 'ri-calculator-line',
      timeframe: '按月申报',
      price: '200-500元/月',
      features: ['税务申报', '税务合规', '税务咨询', '资格认定']
    },
    {
      id: 'social-security',
      title: '社保公积金',
      description: '社保开户、员工增减员、缴费基数申报等',
      icon: 'ri-shield-user-line',
      timeframe: '按月处理',
      price: '50-100元/人月',
      features: ['社保开户', '增减员办理', '缴费申报', '公积金管理']
    },
    {
      id: 'financial-analysis',
      title: '财务分析',
      description: '定期财务分析报告，为企业决策提供数据支持',
      icon: 'ri-line-chart-line',
      timeframe: '按季度',
      price: '500-1500元/次',
      features: ['财务分析', '经营诊断', '风险预警', '改进建议']
    },
    {
      id: 'annual-inspection',
      title: '工商年检',
      description: '工商年报编制、企业信用信息公示等',
      icon: 'ri-bookmark-line',
      timeframe: '年度服务',
      price: '200-500元/年',
      features: ['年报编制', '信息公示', '年检协助', '合规检查']
    },
    {
      id: 'special-services',
      title: '专项服务',
      description: '汇算清缴、出口退税、税务稽查应对等',
      icon: 'ri-award-line',
      timeframe: '按项目',
      price: '1000-5000元/项',
      features: ['汇算清缴', '出口退税', '稽查应对', '专项咨询']
    }
  ];

  const serviceProcess = [
    {
      step: '1',
      title: '初步洽谈',
      description: '了解企业基本情况、行业特点、经营规模和服务需求',
      icon: 'ri-discuss-line',
      details: ['企业基本情况调研', '服务需求分析', '可行性评估', '初步方案制定']
    },
    {
      step: '2', 
      title: '签约准备',
      description: '明确服务内容、期限、费用标准，准备基础材料',
      icon: 'ri-file-text-line',
      details: ['服务合同签订', '费用标准确定', '基础材料收集', '服务流程说明']
    },
    {
      step: '3',
      title: '资料交接',
      description: '企业移交财务资料，建立财务档案和账套',
      icon: 'ri-exchange-line',
      details: ['财务资料移交', '档案建立', '账套设置', '历史数据整理']
    },
    {
      step: '4',
      title: '日常服务',
      description: '按月处理账务，编制报表，完成纳税申报',
      icon: 'ri-calendar-check-line',
      details: ['月度账务处理', '财务报表编制', '税务申报', '问题及时沟通']
    },
    {
      step: '5',
      title: '期末总结',
      description: '提供综合财务分析，协助年度汇算和工商年报',
      icon: 'ri-bar-chart-line',
      details: ['财务分析报告', '年度汇算清缴', '工商年报', '改进建议']
    }
  ];

  const requiredMaterials = [
    {
      category: '基础证照',
      icon: 'ri-file-shield-line', 
      color: 'bg-blue-500',
      items: [
        '营业执照副本复印件',
        '法定代表人身份证复印件',
        '银行开户许可证复印件',
        '公司章程复印件',
        '组织机构代码证（如有）'
      ]
    },
    {
      category: '财务资料',
      icon: 'ri-money-dollar-circle-line',
      color: 'bg-green-500', 
      items: [
        '销售发票、收款凭证等收入类票据',
        '采购发票、费用发票等成本费用票据',
        '员工工资表、社保缴纳单',
        '银行对账单、银行回单',
        '固定资产采购发票、折旧计算表'
      ]
    },
    {
      category: '税务资料',
      icon: 'ri-file-list-2-line',
      color: 'bg-purple-500',
      items: [
        '纳税申报表',
        '税收缴款书',
        '税收优惠备案资料',
        '发票领用存记录',
        '税务登记证（如有）'
      ]
    },
    {
      category: '补充材料',
      icon: 'ri-folder-add-line',
      color: 'bg-orange-500',
      items: [
        '员工劳动合同',
        '社保缴纳明细表',
        '经营场所租赁合同',
        '重要业务合同',
        '其他相关证明文件'
      ]
    }
  ];

  const taxTypes = [
    {
      name: '增值税',
      rate: '13%/9%/6%/3%',
      period: '按月/按季',
      description: '根据企业类型确定税率和申报周期',
      color: 'bg-blue-100 text-blue-800'
    },
    {
      name: '企业所得税', 
      rate: '25%/20%/15%',
      period: '按季预缴',
      description: '小微企业可享受优惠税率',
      color: 'bg-green-100 text-green-800'
    },
    {
      name: '个人所得税',
      rate: '3%-45%',
      period: '按月代扣',
      description: '代扣代缴员工个人所得税',
      color: 'bg-purple-100 text-purple-800'
    },
    {
      name: '城建税及附加',
      rate: '7%+3%+2%',
      period: '随主税申报',
      description: '城建税+教育费附加+地方教育附加',
      color: 'bg-orange-100 text-orange-800'
    }
  ];

  const detailedGuides = {
    'basic-accounting': {
      title: '基础财务服务详细指南',
      sections: [
        {
          title: '账务处理服务',
          icon: 'ri-file-edit-line',
          content: [
            '【票据整理】收集整理企业各类原始票据，包括销售发票、采购发票、费用报销单、银行回单等',
            '【凭证编制】根据原始票据编制规范的会计记账凭证，确保会计科目使用准确',
            '【账簿登记】及时登记各类会计账簿，包括总账、明细账、日记账等',
            '【对账工作】定期与银行对账，核实往来款项，确保账实相符',
            '【报表编制】按月编制资产负债表、利润表、现金流量表等财务报表'
          ]
        },
        {
          title: '账簿管理规范',
          icon: 'ri-archive-line',
          content: [
            '【账簿保管】妥善保管纸质和电子账簿，确保财务数据安全',
            '【档案整理】按年度整理装订会计凭证，建立完整的财务档案',
            '【备份管理】定期备份电子账套数据，防止数据丢失',
            '【查阅服务】支持企业随时调阅查询历史财务数据',
            '【移交清单】服务终止时提供完整的账务移交清单'
          ]
        },
        {
          title: '财务报表分析',
          icon: 'ri-bar-chart-box-line',
          content: [
            '【月度报表】每月及时提供资产负债表、利润表等基础报表',
            '【数据解读】对财务数据进行专业解读，分析经营状况',
            '【趋势分析】比较分析各月度财务数据变化趋势',
            '【异常提醒】发现财务数据异常及时提醒企业关注',
            '【建议反馈】根据财务数据分析结果提出管理建议'
          ]
        },
        {
          title: '审计协助支持',
          icon: 'ri-search-eye-line',
          content: [
            '【资料准备】协助企业准备各类审计所需的财务资料',
            '【问题解答】配合审计人员工作，解答财务相关问题',
            '【整改协助】根据审计意见协助企业进行财务整改',
            '【合规检查】定期检查财务处理的合规性，预防审计风险'
          ]
        }
      ]
    },
    'tax-services': {
      title: '税务服务详细指南',
      sections: [
        {
          title: '税务申报服务',
          icon: 'ri-file-upload-line',
          content: [
            '【增值税申报】按月或按季申报增值税，及时缴纳税款',
            '【企业所得税申报】按季预缴企业所得税，年度汇算清缴',
            '【个人所得税申报】代扣代缴员工个人所得税，按月申报',
            '【其他税种申报】城建税、教育费附加、印花税等其他税种申报',
            '【申报时限管理】严格按照税务部门规定的时限完成各项申报'
          ]
        },
        {
          title: '税务合规管理',
          icon: 'ri-shield-check-line',
          content: [
            '【政策解读】及时关注最新税收政策变化，解读对企业的影响',
            '【合规检查】定期检查企业税务处理的合规性，发现问题及时纠正',
            '【风险评估】评估企业税务风险，制定预防措施',
            '【优化建议】在合法合规前提下，提出税务优化建议',
            '【培训指导】为企业财务人员提供税务知识培训'
          ]
        },
        {
          title: '税务咨询服务',
          icon: 'ri-question-answer-line',
          content: [
            '【日常咨询】解答企业日常经营中遇到的各类税务问题',
            '【政策咨询】解读新出台的税收政策对企业的具体影响',
            '【筹划咨询】为企业重大经营决策提供税务筹划建议',
            '【争议处理】协助企业处理与税务部门之间的争议问题',
            '【专项咨询】针对特殊业务提供专项税务咨询服务'
          ]
        },
        {
          title: '资格认定服务',
          icon: 'ri-medal-line',
          content: [
            '【一般纳税人认定】协助企业申请一般纳税人资格认定',
            '【小规模纳税人登记】办理小规模纳税人相关登记手续',
            '【高新技术企业认定】协助符合条件的企业申请高新技术企业认定',
            '【税收优惠备案】办理各类税收优惠政策的备案手续',
            '【出口退税资格】协助出口企业申请出口退税资格认定'
          ]
        }
      ]
    },
    'social-security': {
      title: '社保公积金详细指南',
      sections: [
        {
          title: '社保开户服务',
          icon: 'ri-user-add-line',
          content: [
            '【开户申请】协助企业到社保部门办理社保开户手续',
            '【资料准备】准备营业执照、组织机构代码证等开户所需材料',
            '【账户激活】完成开户后协助企业激活社保缴费账户',
            '【系统对接】协助企业完成社保系统与企业管理系统的对接',
            '【首次申报】指导企业完成首次社保缴费申报'
          ]
        },
        {
          title: '员工增减员',
          icon: 'ri-group-line',
          content: [
            '【新员工参保】为新入职员工办理社保参保手续',
            '【离职员工停保】为离职员工办理社保停保手续',
            '【信息变更】办理员工个人信息变更手续',
            '【转移手续】协助员工办理社保关系转移手续',
            '【特殊情况处理】处理补缴、断保等特殊情况'
          ]
        },
        {
          title: '缴费基数申报',
          icon: 'ri-calculator-line',
          content: [
            '【基数核定】根据员工工资水平核定社保缴费基数',
            '【年度申报】每年按时完成社保缴费基数年度申报',
            '【基数调整】根据工资变化及时调整缴费基数',
            '【政策执行】严格按照当地社保政策执行缴费标准',
            '【异常处理】处理缴费基数申报中的各类异常情况'
          ]
        },
        {
          title: '公积金管理',
          icon: 'ri-home-heart-line',
          content: [
            '【开户办理】协助企业办理住房公积金开户手续',
            '【缴存申报】按月办理住房公积金缴存申报',
            '【基数调整】根据政策要求调整公积金缴存基数',
            '【员工服务】协助员工办理公积金提取、贷款等业务',
            '【年度结算】配合公积金中心完成年度结算工作'
          ]
        }
      ]
    },
    'financial-analysis': {
      title: '财务分析详细指南',
      sections: [
        {
          title: '盈利能力分析',
          icon: 'ri-funds-line',
          content: [
            '【收入分析】分析企业各项收入的构成、变化趋势和增长质量',
            '【成本分析】分析成本结构、成本控制效果和成本变化原因',
            '【利润分析】分析毛利率、净利率等盈利指标的变化情况',
            '【盈利质量】评估企业盈利的可持续性和真实性',
            '【对标分析】与同行业企业进行盈利能力对比分析'
          ]
        },
        {
          title: '偿债能力分析',
          icon: 'ri-shield-line',
          content: [
            '【流动性分析】分析企业短期偿债能力，评估资金周转状况',
            '【长期偿债分析】分析企业长期偿债能力和财务结构合理性',
            '【债务结构分析】分析债务的期限结构、成本结构和风险状况',
            '【现金流分析】分析经营现金流对债务偿还的保障程度',
            '【风险预警】识别潜在的偿债风险，提出预防措施'
          ]
        },
        {
          title: '营运能力分析',
          icon: 'ri-speed-line',
          content: [
            '【存货周转分析】分析存货管理效率和资金占用情况',
            '【应收账款分析】分析客户信用状况和资金回收效率',
            '【应付账款分析】分析供应商关系和资金使用效率',
            '【总资产周转】评估企业资产使用效率和管理水平',
            '【营运改进】提出改善营运效率的具体建议'
          ]
        },
        {
          title: '发展能力分析',
          icon: 'ri-rocket-line',
          content: [
            '【成长性分析】分析企业收入、利润的成长性和可持续性',
            '【投资分析】评估企业投资项目的效果和回报情况',
            '【创新能力】分析企业研发投入和创新能力',
            '【市场地位】评估企业在行业中的竞争地位',
            '【发展建议】提出促进企业持续发展的战略建议'
          ]
        }
      ]
    },
    'annual-inspection': {
      title: '工商年检详细指南',
      sections: [
        {
          title: '年报基本要求',
          icon: 'ri-calendar-check-line',
          content: [
            '【报送时间】每年1月1日至6月30日，通过国家企业信用信息公示系统报送上一年度年报',
            '【报送主体】在中国境内设立的公司、非公司企业法人、合伙企业、个人独资企业等',
            '【公示义务】年报信息需要向社会公示，体现企业信息透明度',
            '【逾期后果】逾期不报送年报将被列入经营异常名录，影响企业信用',
            '【法律责任】虚假年报信息将承担相应法律责任'
          ]
        },
        {
          title: '年报内容详解',
          icon: 'ri-file-list-3-line',
          content: [
            '【企业基本信息】包括企业名称、统一社会信用代码、注册地址、经营地址、邮政编码、联系电话、电子邮箱等',
            '【股东及出资信息】股东姓名或名称、认缴出资额、出资时间、出资方式、实缴出资额、实际出资时间等',
            '【网站或网店信息】企业拥有的网站或从事网络经营的网店的名称、网址等信息',
            '【企业联系人信息】企业联系人的姓名、联系电话、电子邮箱等',
            '【企业经营状态信息】包括从业人数、资产总额、负债总额、所有者权益合计、营业总收入、净利润、纳税总额等'
          ]
        },
        {
          title: '年报填报流程',
          icon: 'ri-route-line',
          content: [
            '第1步：登录系统 - 访问国家企业信用信息公示系统，选择企业所在地区',
            '第2步：企业信息填报 - 使用企业联络员账号登录，选择"年度报告填报"',
            '第3步：填写年报信息 - 按照系统提示逐项填写各类年报信息',
            '第4步：预览确认 - 填写完成后预览年报内容，确认信息准确无误',
            '第5步：提交公示 - 确认无误后提交，年报信息将向社会公示',
            '第6步：保存凭证 - 打印或保存年报公示凭证，作为完成年报的证明'
          ]
        },
        {
          title: '财务数据填报',
          icon: 'ri-bar-chart-line',
          content: [
            '【资产总额】填报企业年末资产负债表中的资产总计数',
            '【负债总额】填报企业年末资产负债表中的负债合计数',
            '【所有者权益】填报企业年末资产负债表中的所有者权益合计数',
            '【营业总收入】填报企业全年利润表中的营业收入',
            '【净利润】填报企业全年利润表中的净利润数',
            '【纳税总额】填报企业全年实际缴纳的各项税费总额'
          ]
        },
        {
          title: '常见问题处理',
          icon: 'ri-question-line',
          content: [
            'Q: 新设立企业当年是否需要报送年报？A: 新设立企业当年无需报送年报，次年开始报送',
            'Q: 年报中的财务数据如何确定？A: 根据企业会计准则编制的财务报表填写',
            'Q: 企业经营地址与注册地址不一致怎么办？A: 在年报中如实填写实际经营地址',
            'Q: 忘记联络员账号密码怎么办？A: 可通过系统找回功能或到市场监管部门重置',
            'Q: 发现年报信息填写错误如何修改？A: 可在规定期限内登录系统进行修改'
          ]
        },
        {
          title: '异常名录移出',
          icon: 'ri-refresh-line',
          content: [
            '【异常原因】企业未按规定期限公示年报被列入经营异常名录',
            '【移出条件】补报未报年份的年报并公示后，可申请移出异常名录',
            '【申请流程】通过国家企业信用信息公示系统在线申请移出',
            '【审核时限】市场监管部门收到申请后5个工作日内核实并决定是否移出',
            '【信用修复】移出异常名录后，相关信息不再作为失信信息公示'
          ]
        }
      ]
    },
    'special-services': {
      title: '专项服务详细指南',
      sections: [
        {
          title: '汇算清缴服务',
          icon: 'ri-calculator-fill',
          content: [
            '【服务时间】每年3月1日至5月31日，为企业办理上一年度企业所得税汇算清缴',
            '【适用对象】实行查账征收的企业所得税纳税人都需要进行汇算清缴',
            '【主要工作】审核企业全年度财务数据，计算应缴企业所得税，办理多退少补',
            '【税收优惠】帮助企业充分享受小微企业、高新技术企业等各项税收优惠政策',
            '【风险防控】识别和化解汇算清缴过程中的税务风险，确保合规申报'
          ]
        },
        {
          title: '汇算清缴详细流程',
          icon: 'ri-flow-chart',
          content: [
            '第1步：资料收集 - 收集企业全年度的财务报表、账簿、凭证等相关资料',
            '第2步：数据审核 - 审核财务数据的真实性、完整性和合规性',
            '第3步：纳税调整 - 根据税法规定进行各项纳税调整，计算应纳税所得额',
            '第4步：优惠计算 - 计算各项税收优惠金额，确保充分享受政策红利',
            '第5步：申报表填写 - 填写企业所得税年度纳税申报表及相关附表',
            '第6步：网上申报 - 通过电子税务局提交汇算清缴申报表',
            '第7步：税款处理 - 根据计算结果办理补缴税款或申请退税'
          ]
        },
        {
          title: '出口退税服务',
          icon: 'ri-truck-line',
          content: [
            '【资格申请】协助出口企业申请出口退税资格认定，办理相关备案手续',
            '【政策咨询】解读出口退税政策，指导企业合规开展出口业务',
            '【退税计算】准确计算出口货物应退税额，确保企业及时收到退税款',
            '【申报办理】代理企业办理出口退税申报，跟踪办理进度',
            '【风险管控】帮助企业建立出口退税内控制度，防范退税风险'
          ]
        },
        {
          title: '出口退税操作流程',
          icon: 'ri-exchange-box-line',
          content: [
            '第1步：出口报关 - 货物出口时在海关办理出口报关手续，取得出口报关单',
            '第2步：收汇核销 - 收到外汇后到外汇管理局办理收汇核销手续',
            '第3步：开具发票 - 向购货方开具增值税专用发票或普通发票',
            '第4步：资料整理 - 整理出口货物报关单、销售发票、收汇核销单等退税资料',
            '第5步：退税申报 - 通过出口退税申报系统提交退税申请',
            '第6步：审核批复 - 税务部门审核后批复退税金额',
            '第7步：退税到账 - 退税款项直接转入企业银行账户'
          ]
        },
        {
          title: '税务稽查应对',
          icon: 'ri-search-2-line',
          content: [
            '【稽查通知】接到税务稽查通知后，协助企业做好稽查前的准备工作',
            '【资料准备】整理稽查所需的账簿、凭证、报表等各类财务资料',
            '【现场配合】在稽查过程中配合税务人员工作，提供必要的解释说明',
            '【问题解答】对稽查中发现的问题进行专业解答和处理建议',
            '【整改落实】根据稽查结论制定整改方案，督促企业落实整改措施'
          ]
        },
        {
          title: '稽查应对策略',
          icon: 'ri-shield-check-fill',
          content: [
            '【事前预防】建立完善的财税内控制度，定期进行税务风险自查',
            '【积极配合】主动配合税务稽查，提供真实完整的财务资料',
            '【专业沟通】运用专业知识与稽查人员进行有效沟通',
            '【合理申辩】对稽查中的争议问题进行合理申辩和解释',
            '【及时整改】对确实存在的问题及时整改，避免重复违规'
          ]
        },
        {
          title: '专项税务咨询',
          icon: 'ri-lightbulb-fill',
          content: [
            '【重组改制】为企业重组、改制、合并、分立等提供税务筹划服务',
            '【股权转让】为股权转让、股权激励等业务提供税务处理建议',
            '【跨境业务】为企业对外投资、境外所得等跨境业务提供税务指导',
            '【新业务模式】为电商、直播、共享经济等新业务模式提供税务解决方案',
            '【政策解读】及时解读新出台的税收政策，分析对企业的影响'
          ]
        },
        {
          title: '税务风险管理',
          icon: 'ri-alert-fill',
          content: [
            '【风险识别】定期开展税务风险排查，识别潜在的税务风险点',
            '【风险评估】对识别的风险进行等级评估，制定相应的应对措施',
            '【内控建设】帮助企业建立健全税务内控制度和风险管理体系',
            '【培训指导】为企业财务人员提供税务风险防控培训',
            '【持续监控】建立税务风险监控机制，及时发现和处理新的风险'
          ]
        }
      ]
    }
  };

  // 如果选择了详细指南，显示指南内容
  if (selectedGuide && detailedGuides[selectedGuide as keyof typeof detailedGuides]) {
    const guide = detailedGuides[selectedGuide as keyof typeof detailedGuides];
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100">
        {/* Header */}
        <header className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button 
                  onClick={() => setSelectedGuide(null)}
                  className="text-green-600 hover:text-green-800 cursor-pointer"
                >
                  <i className="ri-arrow-left-line text-xl"></i>
                </button>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">{guide.title}</h1>
                  <p className="text-lg text-gray-600 mt-2">专业财税服务操作指南</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <button className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap">
                  收藏指南
                </button>
                <button className="border border-green-600 text-green-600 px-6 py-2 rounded-lg hover:bg-green-50 transition-colors cursor-pointer whitespace-nowrap">
                  打印指南
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Guide Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Navigation Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-lg p-6 sticky top-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">指南目录</h3>
                <nav className="space-y-2">
                  {guide.sections.map((section, index) => (
                    <a
                      key={index}
                      href={`#section-${index}`}
                      className="flex items-center p-3 text-gray-700 hover:bg-green-50 hover:text-green-700 rounded-lg transition-colors cursor-pointer"
                    >
                      <i className={`${section.icon} mr-3 text-green-600`}></i>
                      <span className="text-sm font-medium">{section.title}</span>
                    </a>
                  ))}
                </nav>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              <div className="space-y-8">
                {guide.sections.map((section, index) => (
                  <div
                    key={index}
                    id={`section-${index}`}
                    className="bg-white rounded-xl shadow-lg overflow-hidden"
                  >
                    <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-6 py-4">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center mr-4">
                          <i className={`${section.icon} text-white text-xl`}></i>
                        </div>
                        <h3 className="text-xl font-bold text-white">{section.title}</h3>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="space-y-4">
                        {section.content.map((item, itemIndex) => (
                          <div key={itemIndex} className="flex items-start">
                            <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                            <p className="text-gray-700 leading-relaxed">{item}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="mt-8 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl p-6 text-white">
                <div className="text-center">
                  <h3 className="text-2xl font-bold mb-4">需要专业协助？</h3>
                  <p className="text-green-100 mb-6">我们的专业财税顾问随时为您提供咨询服务</p>
                  <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                    <button className="bg-white text-green-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap">
                      在线咨询顾问
                    </button>
                    <button className="border-2 border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white hover:text-green-600 transition-colors cursor-pointer whitespace-nowrap">
                      预约服务方案
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => window.REACT_APP_NAVIGATE('/')}
                className="text-green-600 hover:text-green-800 cursor-pointer"
              >
                <i className="ri-arrow-left-line text-xl"></i>
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">财税代理服务培训</h1>
                <p className="text-lg text-gray-600 mt-2">专业财务和税务解决方案全面指导</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="bg-green-100 px-4 py-2 rounded-full">
                <span className="text-green-800 font-medium">学习进度: 0%</span>
              </div>
              <button className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap">
                开始学习
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-16 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/d50b436a8fa9a4b236ab7714e3d95b32.jpg')`
          }}
        >
          <div className="absolute inset-0 bg-green-900/70"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            专业财税代理服务
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-3xl mx-auto">
            为企业提供全方位财务和税务解决方案，助力企业合规经营和健康发展
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-white">
              <div className="text-2xl font-bold mb-2">专业团队</div>
              <div className="text-green-100">持证会计师</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-white">
              <div className="text-2xl font-bold mb-2">全程服务</div>
              <div className="text-green-100">一站式解决</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-white">
              <div className="text-2xl font-bold mb-2">合规保障</div>
              <div className="text-green-100">风险防控</div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Overview */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">服务内容概述</h3>
            <p className="text-lg text-gray-600">为中小企业、初创公司提供专业财税支持</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {serviceTypes.map((service) => (
              <div key={service.id} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden group">
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-14 h-14 bg-green-100 rounded-lg flex items-center justify-center mr-4">
                      <i className={`${service.icon} text-green-600 text-2xl`}></i>
                    </div>
                    <div>
                      <h4 className="text-xl font-bold text-gray-900">{service.title}</h4>
                      <p className="text-gray-600 text-sm">{service.description}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <h5 className="font-semibold text-gray-900 mb-1">服务周期</h5>
                      <p className="text-green-600 font-medium text-sm">{service.timeframe}</p>
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-900 mb-1">参考价格</h5>
                      <p className="text-green-600 font-medium text-sm">{service.price}</p>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <h5 className="font-semibold text-gray-900 mb-2">主要服务</h5>
                    <div className="flex flex-wrap gap-2">
                      {service.features.map((feature, index) => (
                        <span 
                          key={index}
                          className="bg-green-50 text-green-700 px-3 py-1 rounded-full text-sm"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <button 
                    onClick={() => setSelectedGuide(service.id)}
                    className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap group-hover:bg-green-700"
                  >
                    查看详细指南
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tax Types */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">主要税种及申报</h3>
            <p className="text-lg text-gray-600">掌握各税种特点，确保合规申报</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {taxTypes.map((tax, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6">
                <div className="text-center mb-4">
                  <h4 className="text-lg font-bold text-gray-900 mb-2">{tax.name}</h4>
                  <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${tax.color}`}>
                    {tax.rate}
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">申报周期:</span>
                    <span className="font-medium text-gray-900">{tax.period}</span>
                  </div>
                  <p className="text-gray-600 text-sm">{tax.description}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-8 bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg">
            <div className="flex items-center mb-2">
              <i className="ri-information-line text-amber-600 text-xl mr-2"></i>
              <h4 className="text-lg font-semibold text-amber-800">税务申报提醒</h4>
            </div>
            <p className="text-amber-700">
              各税种都有严格的申报期限，逾期申报将面临滞纳金和罚款。建议企业建立税务申报日历，
              确保按时完成各项申报义务。小规模纳税人可选择按季申报增值税。
            </p>
          </div>
        </div>
      </section>

      {/* Service Process */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">服务流程详解</h3>
            <p className="text-lg text-gray-600">专业规范的服务流程，确保服务质量</p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {serviceProcess.map((process, index) => (
              <div key={process.step} className="text-center">
                <div className="relative mb-6">
                  <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className={`${process.icon} text-white text-2xl`}></i>
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-green-800 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-sm">{process.step}</span>
                  </div>
                  {index < serviceProcess.length - 1 && (
                    <div className="hidden lg:block absolute top-10 left-full w-full h-0.5 bg-green-200 -ml-10"></div>
                  )}
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">{process.title}</h4>
                <p className="text-gray-600 mb-4">{process.description}</p>
                <div className="space-y-1">
                  {process.details.map((detail, detailIndex) => (
                    <div key={detailIndex} className="text-sm text-gray-500">
                      • {detail}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Required Materials */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">企业需提供的材料</h3>
            <p className="text-lg text-gray-600">完整的材料清单，确保服务顺利开展</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {requiredMaterials.map((category, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className={`${category.color} p-4`}>
                  <div className="flex items-center justify-center text-white">
                    <i className={`${category.icon} text-2xl mr-3`}></i>
                    <h4 className="text-lg font-bold">{category.category}</h4>
                  </div>
                </div>
                <div className="p-6">
                  <ul className="space-y-3">
                    {category.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start">
                        <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span className="text-gray-700 text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-8 bg-blue-50 rounded-xl p-6">
            <div className="flex items-center mb-4">
              <i className="ri-lightbulb-line text-blue-600 text-2xl mr-3"></i>
              <h4 className="text-xl font-bold text-gray-900">材料准备建议</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-gray-700">
              <div>
                <h5 className="font-semibold mb-2">电子版要求：</h5>
                <ul className="space-y-1 text-sm">
                  <li>• 图片清晰可见，文字可识别</li>
                  <li>• 文件格式支持PDF、JPG、PNG</li>
                  <li>• 单个文件大小不超过10MB</li>
                  <li>• 证件照片需要正反面完整</li>
                </ul>
              </div>
              <div>
                <h5 className="font-semibold mb-2">交接注意事项：</h5>
                <ul className="space-y-1 text-sm">
                  <li>• 原始票据按月份整理</li>
                  <li>• 重要合同单独标注</li>
                  <li>• 银行回单按账户分类</li>
                  <li>• 及时补充缺失材料</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Important Notes */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">重要注意事项</h3>
            <p className="text-lg text-gray-600">选择财税代理服务的关键要点</p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
                <div className="flex items-center mb-3">
                  <i className="ri-shield-check-line text-red-600 text-xl mr-2"></i>
                  <h4 className="text-lg font-semibold text-red-800">资质核查</h4>
                </div>
                <p className="text-red-700 text-sm">
                  选择财税代理服务时，务必核查服务机构是否持有财政部门颁发的代理记账许可证，
                  从业人员是否具备会计从业资格证书或中级会计师等专业资格。
                </p>
              </div>
              
              <div className="bg-orange-50 border-l-4 border-orange-500 p-6 rounded-r-lg">
                <div className="flex items-center mb-3">
                  <i className="ri-file-text-line text-orange-600 text-xl mr-2"></i>
                  <h4 className="text-lg font-semibold text-orange-800">合同约定</h4>
                </div>
                <p className="text-orange-700 text-sm">
                  签订服务合同前，明确约定服务范围、服务标准、保密条款和责任划分，
                  特别要约定因代理方工作失误造成损失时的赔偿责任和方式。
                </p>
              </div>
            </div>
            
            <div className="space-y-6">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
                <div className="flex items-center mb-3">
                  <i className="ri-folder-line text-blue-600 text-xl mr-2"></i>
                  <h4 className="text-lg font-semibold text-blue-800">票据管理</h4>
                </div>
                <p className="text-blue-700 text-sm">
                  企业应建立健全内部票据管理制度，按时向代理机构提供真实、完整、合规的原始凭证，
                  并定期核对账目，关注自身的财务状况和经营成果。
                </p>
              </div>
              
              <div className="bg-purple-50 border-l-4 border-purple-500 p-6 rounded-r-lg">
                <div className="flex items-center mb-3">
                  <i className="ri-user-star-line text-purple-600 text-xl mr-2"></i>
                  <h4 className="text-lg font-semibold text-purple-800">责任意识</h4>
                </div>
                <p className="text-purple-700 text-sm">
                  即使委托了专业的财税代理机构，企业的法定代表人仍然是企业财税合规的第一责任人，
                  应当保持对财务工作的关注和监督。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-gradient-to-r from-green-600 to-emerald-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-white mb-4">选择财税代理的优势</h3>
            <p className="text-xl text-green-100">专业服务带来的多重价值</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-money-dollar-circle-line text-white text-2xl"></i>
              </div>
              <h4 className="text-lg font-bold text-white mb-2">节省成本</h4>
              <p className="text-green-100 text-sm">避免招聘专职财务人员的成本</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-user-star-line text-white text-2xl"></i>
              </div>
              <h4 className="text-lg font-bold text-white mb-2">专业保障</h4>
              <p className="text-green-100 text-sm">获得专业团队的财税指导</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-white text-2xl"></i>
              </div>
              <h4 className="text-lg font-bold text-white mb-2">合规经营</h4>
              <p className="text-green-100 text-sm">降低税务风险和法律风险</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-focus-3-line text-white text-2xl"></i>
              </div>
              <h4 className="text-lg font-bold text-white mb-2">专注主业</h4>
              <p className="text-green-100 text-sm">让企业专注核心业务发展</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h3 className="text-3xl font-bold text-white mb-4">
            专业财税服务助力企业发展
          </h3>
          <p className="text-xl text-gray-300 mb-8">
            通过专业的财税代理服务，企业不仅可以节省成本，还能获得专业指导，降低风险
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <button className="bg-green-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap">
              在线咨询服务
            </button>
            <button className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">
              获取服务方案
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h4 className="text-lg font-semibold mb-4">联系我们</h4>
              <div className="space-y-2 text-gray-300">
                <p>电话: 020-1234-5678</p>
                <p>邮箱: tax@example.com</p>
                <p>地址: 广州市天河区xxx路123号</p>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">快速链接</h4>
              <div className="space-y-2 text-gray-300">
                <p className="cursor-pointer hover:text-white" onClick={() => window.REACT_APP_NAVIGATE('/')}>返回首页</p>
                <p>服务报价</p>
                <p>常见问题</p>
                <p>政策解读</p>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">财税服务</h4>
              <div className="space-y-2 text-gray-300">
                <p>代理记账</p>
                <p>税务申报</p>
                <p>财务分析</p>
                <p>税务筹划</p>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">增值服务</h4>
              <div className="space-y-2 text-gray-300">
                <p>社保代缴</p>
                <p>工商年检</p>
                <p>审计服务</p>
                <p><a href="https://readdy.ai/?origin=logo" className="hover:text-white transition-colors">Made with Readdy</a></p>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 企服培训平台. 保留所有权利.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
